import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInstallPrompt } from '../../hooks/useInstallPrompt'
import {
  readNudgeState,
  recordDismissed,
  recordInstalled,
  recordShown,
  shouldShowNudge,
  writeNudgeState,
} from '../../lib/installNudge'

/**
 * Quiet, dismissible invitation to add the app to the home screen.
 *
 * Still an invitation rather than a nag, but with a small budget instead of
 * one-and-done: at most 3 asks, at least a week apart, then never again (see
 * lib/installNudge). One stray tap on the × no longer costs the install
 * forever.
 *
 * Uniform across platforms — same budget and cadence everywhere. Only the
 * button differs: Android/desktop get the native prompt, iOS gets the manual
 * Add-to-Home-Screen guide, because iOS has no programmatic install.
 *
 * Still renders only where there is actually something to offer (`canPrompt ||
 * isIos`); a browser with no install path would otherwise get a dead button.
 */
export function InstallCard() {
  const { canPrompt, isInstalled, isIos, promptInstall } = useInstallPrompt()
  const [state, setState] = useState(readNudgeState)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  const offerable = canPrompt || isIos
  const visible = !isInstalled && offerable && shouldShowNudge(state)

  // Persist the install the moment we detect it. `display-mode: standalone` is
  // only true INSIDE the installed app, so without this the same device in a
  // plain browser tab looks uninstalled and would be asked again.
  useEffect(() => {
    if (!isInstalled || state.stopped) return
    const next = recordInstalled(state)
    setState(next)
    writeNudgeState(next)
  }, [isInstalled, state])

  // Starting the cooldown is a side effect of actually being seen, so it is
  // recorded once per mount that renders — not on every re-render.
  const markedRef = useRef(false)
  useEffect(() => {
    if (!visible || markedRef.current) return
    markedRef.current = true
    writeNudgeState(recordShown(readNudgeState()))
  }, [visible])

  if (!visible) return null

  const dismiss = () => {
    const next = recordDismissed(readNudgeState())
    setState(next)
    writeNudgeState(next)
  }

  const handleInstall = async () => {
    setBusy(true)
    try {
      await promptInstall()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="install-card"
      className="relative mt-3 bg-cosmic-card-warm rounded-xl p-3.5 pr-10 border border-gold/25"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-1.5 right-1.5 p-2 -m-1 text-text-secondary hover:text-text-primary leading-none text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded"
      >
        ×
      </button>
      <p className="text-sm text-text-primary font-display leading-relaxed">
        Keep The Cosmic Finder on your home screen
      </p>
      <p className="text-xs text-text-secondary font-body leading-relaxed mt-1">
        One tap to your daily card, the sky right now, and ceremony reminders.
      </p>
      {canPrompt ? (
        <button
          type="button"
          onClick={handleInstall}
          disabled={busy}
          className="mt-2 text-xs tracking-cosmic uppercase font-medium text-gold-light hover:text-gold transition-colors disabled:opacity-60 disabled:cursor-wait focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm"
        >
          {busy ? 'Installing…' : 'Install the app'}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/install')}
          className="mt-2 text-xs tracking-cosmic uppercase font-medium text-gold-light hover:text-gold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm"
        >
          Show me how
        </button>
      )}
    </div>
  )
}
